import { Compare, useUserContext } from '@nexus/fff-utilities';
import { useState } from 'react';
import { track } from '@nexus/fff-analytics';
import { invalidateMenuCache } from '@nexus/fff-global-nav';
import useInviteContext from './useInviteContext';
import { reactivateExpiredSubscription, updateSubscription } from '../services/subscription';
import { getCouponCode, syncDelay } from '../helpers';
import { Plans } from '../pages/constants';

const useShowErrorOrRedirectThankYouPage = (history, thankYouPagePath, currentPlan, couponsApplied = []) => {
  const { invalidateUserCache, userInfo } = useUserContext();
  const { inviteInfo } = useInviteContext();

  const [currentState, setCurrentState] = useState({
    showPayment: false,
    errorPaymentMessage: '',
    showRetry: false,
    isLoading: false,
  });

  const retry = (error, response, sendEvent) => {
    // eslint-disable-next-line no-console
    console.error(error, response);
    sendEvent('failed', () => {
      setCurrentState({
        showPayment: false,
        errorPaymentMessage: '',
        showRetry: true,
        isLoading: false,
      });
    });
  };
  const onClose = () => {
    setCurrentState({
      showPayment: false,
      errorPaymentMessage: '',
      showRetry: false,
      isLoading: false,
    });
  };

  const trackConfirmPurchaseEvent = (status) => {
    track({
      trackName: 'Membership Activated',
      properties: {
        campaign_id: inviteInfo.campaignId,
        campaign_name: inviteInfo.campaignName,
        team_id: inviteInfo.teamId,
        invite_id: inviteInfo.inviteId,
        status: status ? 'success' : 'fail',
        cta_name: 'CONFIRM PURCHASE',
        role: (userInfo.user.role || '').toLowerCase(),
        subscription_state: (userInfo.user.subscriptionState || '').toLowerCase(),
      },
    });
  };

  const trackConfirmPurchaseEventAfterPaySubscriptionResponse = (response) => {
    if (currentPlan === Plans.SOCIAL) {
      if (response) {
        trackConfirmPurchaseEvent(true);
      } else {
        trackConfirmPurchaseEvent(false);
      }
    }
  };

  const handlePaySubscriptionResponse = (response, sendEvent) => {
    if (response.ok) {
      trackConfirmPurchaseEventAfterPaySubscriptionResponse(true);
      sendEvent('success', () => {
        invalidateUserCache();
        syncDelay(1000);
        invalidateMenuCache();
        syncDelay(4000);
        if (Compare.isObject(thankYouPagePath)) {
          history.push({
            ...thankYouPagePath,
            state: { coupon_code: getCouponCode(couponsApplied) },
          });
        } else {
          history.push({
            pathname: thankYouPagePath,
            state: { coupon_code: getCouponCode(couponsApplied) },
          });
        }
      });
    } else if (response.status === 422) {
      trackConfirmPurchaseEventAfterPaySubscriptionResponse(false);
      response.json().then((body) => {
        if (body.is_transaction_error) {
          sendEvent('payment failed', () => (
            setCurrentState({
              showPayment: true,
              errorPaymentMessage: body.customer_message,
              showRetry: false,
              isLoading: false,
            })));
        } else {
          retry('It is not a transaction_error', response, sendEvent);
        }
      }).catch((error) => {
        retry(error, response, sendEvent);
      });
    } else {
      trackConfirmPurchaseEventAfterPaySubscriptionResponse(false);
      retry('It is not a 422', response, sendEvent);
    }
  };

  const sendSegmentEventAfterResponse = (ctaName, planSelected, status, callback) => {
    track({
      trackName: ctaName,
      properties: { plan_selected: planSelected, status, coupon_code: getCouponCode(couponsApplied) },
    }).then(callback)
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn(e);
        callback();
      });
  };

  const sendGTMReactivateConversion = (response) => {
    if (!response.ok) {
      return;
    }
    response.json().then((body) => {
      window.dataLayer.push({
        transactionId: body.invoice_number,
        transactionAffiliation: 'Reactivate Subscription',
        transactionTotal: body.total_charge_amount,
        transactionTax: body.tax_amount,
        transactionShipping: body.shipping_fee_amount,
        transactionProducts: [{
          sku: body.plan_code,
          name: 'FabFitFun VIP Reactivate',
          category: 'Subscription Box',
          price: body.total_charge_amount,
          quantity: 1,
        }],
        productLabel: 'VIP Reactivate',
        event: 'vip_reactivate',
      });
    }).catch((e) => {
      // eslint-disable-next-line no-console
      console.warn(e);
    });
  };
  const changeSubscription = (plan) => {
    setCurrentState({ ...currentState, isLoading: true });
    const sendUpdateLogsSegmentEvent = (status, callback) => sendSegmentEventAfterResponse(
      'Upgrade Logs', plan, status, callback,
    );
    updateSubscription(plan, currentPlan, couponsApplied)
      .then((response) => handlePaySubscriptionResponse(response,
        sendUpdateLogsSegmentEvent))
      .catch((error) => (retry(error, sendUpdateLogsSegmentEvent)));
  };

  const changeReactivateSubscription = (plan) => {
    setCurrentState({ ...currentState, isLoading: true });
    const sendVipReactivateSegmentEvent = (status, callback) => sendSegmentEventAfterResponse(
      'VIP Reactivate', plan, status, callback,
    );
    reactivateExpiredSubscription(plan, couponsApplied)
      .then((response) => {
        sendGTMReactivateConversion(response);
        syncDelay(200);
        handlePaySubscriptionResponse(response, sendVipReactivateSegmentEvent);
      })
      .catch((error) => (retry(error, sendVipReactivateSegmentEvent)));
  };

  return {
    ...currentState, changeSubscription, onClose, changeReactivateSubscription,
  };
};

export default useShowErrorOrRedirectThankYouPage;
